import {
  Badge,
  Card,
  Group,
  Loader,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Search } from "lucide-react";

import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api/client";
import { formatDate } from "@/lib/format";

interface AdminPatientRow {
  id: string;
  clinic_id: string;
  clinic_name: string;
  card_number: string;
  family_name: string;
  given_name: string;
  birth_date: string | null;
  sex: string | null;
  workplace: string | null;
  position: string | null;
  created_at: string;
}

export function AdminPatientsPage() {
  const [search, setSearch] = useState("");
  const [debSearch] = useDebouncedValue(search, 250);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-all-patients"],
    queryFn: async () => {
      const { data } = await api.get<AdminPatientRow[]>("/admin/patients");
      return data;
    },
  });

  const filtered = (data ?? []).filter((p) => {
    if (!debSearch.trim()) return true;
    const q = debSearch.toLowerCase();
    return (
      p.family_name.toLowerCase().includes(q) ||
      p.given_name.toLowerCase().includes(q) ||
      p.card_number.toLowerCase().includes(q) ||
      p.clinic_name.toLowerCase().includes(q) ||
      (p.workplace ?? "").toLowerCase().includes(q) ||
      (p.position ?? "").toLowerCase().includes(q)
    );
  });

  return (
    <Stack gap="lg">
      <PageHeader
        title="Barcha bemorlar"
        subtitle={`${filtered.length} bemor`}
      />

      <Card padding="md">
        <TextInput
          rightSection={<Search size={18} strokeWidth={2} />}
          placeholder="F.I.SH, karta, klinika, ish joyi..."
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />
      </Card>

      <Card padding={0} radius="xl">
        <Table.ScrollContainer minWidth={1000}>
          <Table verticalSpacing="md" striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Klinika</Table.Th>
                <Table.Th>Karta №</Table.Th>
                <Table.Th>F.I.SH</Table.Th>
                <Table.Th>Tug'ilgan</Table.Th>
                <Table.Th>Jinsi</Table.Th>
                <Table.Th>Ish joyi</Table.Th>
                <Table.Th>Lavozim</Table.Th>
                <Table.Th>Yaratilgan</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {isLoading && (
                <Table.Tr><Table.Td colSpan={8}><Loader size="sm" /></Table.Td></Table.Tr>
              )}
              {filtered.map((p) => (
                <Table.Tr key={p.id}>
                  <Table.Td>
                    <Badge variant="light" color="brand" radius="sm">
                      {p.clinic_name}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" ff="monospace" fw={600}>{p.card_number}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" fw={600}>{p.family_name} {p.given_name}</Text>
                  </Table.Td>
                  <Table.Td><Text size="xs" c="dimmed">{p.birth_date ?? "—"}</Text></Table.Td>
                  <Table.Td><Text size="sm">{p.sex ?? "—"}</Text></Table.Td>
                  <Table.Td><Text size="sm">{p.workplace ?? "—"}</Text></Table.Td>
                  <Table.Td><Text size="sm">{p.position ?? "—"}</Text></Table.Td>
                  <Table.Td><Text size="xs" c="dimmed">{formatDate(p.created_at)}</Text></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>
    </Stack>
  );
}
